import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import {CloseOutlined, SearchOutlined} from '@material-ui/icons';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import useErrorMessage from '../../../../hooks/useErrorMessage.js';
import {arquivoParaBase64} from '../../../../utils/arquivo.js';
import {
    MENSAGEM_ARQUIVO_NAO_LIDO,
    MENSAGEM_ARQUIVO_NAO_SELECIONADO,
    montarArquivo,
    validarArquivoXlsx
} from './cargaSaf.js';

const useStyles = makeStyles(theme => ({
    linha: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(2)
    },
    inputEscondido: {
        display: 'none'
    },
    nome: {
        wordBreak: 'break-all'
    }
}));

/*
 * Seleção de uma planilha xlsx (até 10 MB) das telas de Carga SAF: valida
 * extensão e tamanho, lê o arquivo em base64 e devolve {nomeArquivo, conteudo}
 * em `onSelecionar`. O arquivo escolhido fica na tela que usa o componente
 * (`arquivo`), que também o limpa em `onRemover`.
 */
export default function SeletorArquivoXlsx({id, rotulo, arquivo, onSelecionar, onRemover, disabled = false}) {
    const classes = useStyles();
    const showErrorMessage = useErrorMessage();
    const inputRef = useRef(null);

    const aoEscolher = async evento => {
        // O input é reciclado pelo React: guarda o nó e o arquivo antes do primeiro await.
        const input = evento.target;
        const escolhido = input.files && input.files[0];
        input.value = '';

        if (!escolhido) {
            return;
        }

        const erro = validarArquivoXlsx(escolhido);
        if (erro) {
            showErrorMessage(erro);
            return;
        }

        try {
            const conteudo = await arquivoParaBase64(escolhido);
            onSelecionar(montarArquivo(escolhido.name, conteudo));
        } catch (e) {
            showErrorMessage(MENSAGEM_ARQUIVO_NAO_LIDO);
        }
    };

    return (
        <LabelValue label={rotulo}>
            <div className={classes.linha}>
                <input
                    id={id}
                    ref={inputRef}
                    type='file'
                    accept='.xlsx'
                    className={classes.inputEscondido}
                    onChange={aoEscolher}/>
                <ContainedButton startIcon={<SearchOutlined/>} disabled={disabled} onClick={() => inputRef.current.click()}>
                    Selecionar
                </ContainedButton>
                <Typography variant='body1' className={classes.nome}>
                    {arquivo ? arquivo.nomeArquivo : MENSAGEM_ARQUIVO_NAO_SELECIONADO}
                </Typography>
                {!!arquivo && (
                    <TextButton startIcon={<CloseOutlined/>} disabled={disabled} onClick={onRemover}>
                        Remover
                    </TextButton>
                )}
            </div>
        </LabelValue>
    );
}

SeletorArquivoXlsx.propTypes = {
    id: PropTypes.string.isRequired,
    rotulo: PropTypes.string.isRequired,
    arquivo: PropTypes.shape({nomeArquivo: PropTypes.string, conteudo: PropTypes.string}),
    onSelecionar: PropTypes.func.isRequired,
    onRemover: PropTypes.func.isRequired,
    disabled: PropTypes.bool
};
